/**
 * Lightweight CTA analytics client.
 *
 * On first page visit we capture any utm_* query parameters into
 * sessionStorage so subsequent CTA clicks in the same session are still
 * attributed to the original acquisition source, even after the visitor
 * navigates deeper into the site.
 *
 * trackEvent(name, meta) fires a fire-and-forget POST to /api/events/track.
 * Never throws — analytics must never break a real user action.
 */
import axios from 'axios';
import { logWarn } from './logger';

const STORAGE_KEY = 'ctrl-utm';
const SESSION_ID_KEY = 'ctrl-analytics-session';
const CONSENT_KEY = 'ctrl-aero-cookie-consent';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND ? `${BACKEND}/api` : '/api';

const genSessionId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const getSessionId = () => {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = genSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, sid);
    }
    return sid;
  } catch (e) {
    logWarn('analytics: sessionStorage unavailable', e);
    return undefined;
  }
};

const readStoredUtms = () => {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    logWarn('analytics: could not read stored UTMs', e);
    return {};
  }
};

/**
 * Capture utm_* params from the current URL and persist to sessionStorage
 * (only the first time — the original acquisition source wins).  Safe to
 * call on every mount; a no-op if no UTMs are present or if we've already
 * captured them this session.
 */
export const captureUtms = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = readStoredUtms();
    const next = { ...stored };
    let changed = false;
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val && !stored[key]) {
        next[key] = val.slice(0, 120);
        changed = true;
      }
    }
    if (changed) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  } catch (e) {
    logWarn('analytics: UTM capture failed', e);
  }
};

const readConsent = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw).choice || null;
  } catch (e) {
    logWarn('analytics: could not read consent', e);
    return null;
  }
};

/** Map a product page's productName to a compact canonical label so the
 *  "Top products" panel doesn't split e.g. "UL101" and "CTRL UL101
 *  Aviation Troubleshooter" into two rows. */
export const canonicalProduct = (name) => {
  if (!name) return null;
  const s = String(name);
  if (/UL101/i.test(s)) return 'UL101';
  if (/FOTRIC/i.test(s)) return 'FOTRIC Acoustic';
  if (/Dalcomm/i.test(s)) return 'Dalcomm';
  return s.slice(0, 60);
};

/**
 * Fire a CTA event.  `name` must be one of:
 *   request_demo_click | request_quote_click |
 *   gated_pdf_unlock | warranty_register_open | video_unmute | video_watch_full
 * `meta` may include: product, resource.
 *
 * Honors the visitor's cookie-consent choice:
 *   - 'accepted-all'     → full event (UTMs + session_id)
 *   - 'essential-only'   → send anonymized event (no session_id, no UTMs)
 *   - 'rejected' / null  → no-op (send nothing)
 */
export const trackEvent = (name, meta = {}) => {
  if (typeof window === 'undefined' || window.__PRERENDER__) return;
  const consent = readConsent();
  if (consent !== 'accepted-all' && consent !== 'essential-only') return;
  try {
    const utms = consent === 'accepted-all' ? readStoredUtms() : {};
    const payload = {
      event_name: name,
      page_path: window.location.pathname || '/',
      referrer: consent === 'accepted-all' ? (document.referrer || '').slice(0, 500) : '',
      session_id: consent === 'accepted-all' ? getSessionId() : null,
      product: meta.product ? canonicalProduct(meta.product) : null,
      resource: meta.resource ? String(meta.resource).slice(0, 200) : null,
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      utm_term: utms.utm_term || null,
      utm_content: utms.utm_content || null,
    };
    // fire-and-forget; do not await
    axios.post(`${API}/events/track`, payload, { timeout: 4000 }).catch((e) => logWarn('analytics: track failed', e));
  } catch (e) {
    logWarn('analytics: track skipped', e);
  }
};

export default { trackEvent, captureUtms };
